import React, { useMemo } from 'react';
import { Text } from '@nextui-org/react';
import { AdminSessionDetail } from '../../../types/sessions';

interface Props {
    detail: AdminSessionDetail;
}

const MetricsTab: React.FC<Props> = ({ detail }) => {

    const metrics = useMemo(() => {
        const turns = detail.turns || [];
        const coachTurns = turns.filter(t => t.speaker === 'coach');

        const totalTurns = turns.length;

        let start = 0;
        let end = 0;
        if (turns.length > 0) {
            start = new Date(turns[0].created_at).getTime();
            end = new Date(turns[turns.length - 1].created_at).getTime();
        }
        const effectiveDurationS = Math.max(0, Math.floor((end - start) / 1000));

        const responseTimes = coachTurns.map(t => t.response_time_ms).filter(t => t !== null) as number[];
        const avgResponseTimeMs = responseTimes.length > 0 ? (responseTimes.reduce((a,b)=>a+b, 0) / responseTimes.length).toFixed(0) : 0;

        let totalTokensIn = 0;
        let totalTokensOut = 0;
        coachTurns.forEach(t => {
            if (t.token_count_input) totalTokensIn += t.token_count_input;
            if (t.token_count_output) totalTokensOut += t.token_count_output;
        });

        // Mode distribution
        const modes: Record<string, number> = {};
        coachTurns.forEach(t => {
            if (t.mode) {
                modes[t.mode] = (modes[t.mode] || 0) + 1;
            }
        });

        // % Socratic Annotated
        let socraticCount = 0;
        let annotatedCount = 0;
        coachTurns.forEach(t => {
            if (t.annotations && t.annotations.length > 0) {
                annotatedCount++;
                if (t.annotations.some(a => a.is_socratic)) {
                    socraticCount++;
                }
            }
        });
        const pctSocratic = annotatedCount > 0 ? ((socraticCount / annotatedCount) * 100).toFixed(0) + '%' : 'Not rated yet';

        return {
            totalTurns,
            effectiveDurationS,
            avgResponseTimeMs,
            totalTokens: totalTokensIn + totalTokensOut,
            modes,
            pctSocratic
        };
    }, [detail.turns]);

    const formatDuration = (s: number) => {
        const m = Math.floor(s/60);
        const sec = s % 60;
        return `${m}m ${sec}s`;
    };

    const renderMetricRow = (label: string, value: React.ReactNode, tooltip: string) => (
        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.75rem 0', borderBottom: '1px solid #eaeaea' }} title={tooltip}>
            <Text b size={14}>{label}</Text>
            <Text size={14} color={value === 'Not rated yet' ? '#888' : 'inherit'}>{value}</Text>
        </div>
    );

    return (
        <div style={{ padding: '1rem', maxWidth: '500px' }}>
             {renderMetricRow('Total Turns', metrics.totalTurns, 'Total number of turns from user and coach')}
             {renderMetricRow('Effective Duration', formatDuration(metrics.effectiveDurationS), 'Time from first to last turn')}
             {renderMetricRow('Avg Coach Response Time', `${metrics.avgResponseTimeMs} ms`, 'Average response_time_ms from coach turns')}
             {renderMetricRow('Total Tokens', metrics.totalTokens, 'Sum of input and output tokens')}
             {renderMetricRow('% Socratic (Annotated)', metrics.pctSocratic, 'Percentage of coach turns annotated as is_socratic=true')}

             <div style={{ marginTop: '1.5rem' }}>
                 <Text b size={14}>Mode Distribution</Text>
                 <div style={{ display: 'flex', gap: '1rem', marginTop: '0.5rem' }}>
                     {Object.entries(metrics.modes).map(([mode, count]) => (
                         <div key={mode} style={{ background: '#f4f4f5', padding: '0.25rem 0.5rem', borderRadius: '4px' }}>
                             <Text size={12}>{mode}: {count}</Text>
                         </div>
                     ))}
                     {Object.keys(metrics.modes).length === 0 && <Text size={12} color="#888">No modes recorded.</Text>}
                 </div>
             </div>

             <div style={{ marginTop: '1.5rem', opacity: 0.5 }}>
                 <Text b size={14}>Artifact Evaluation</Text>
                 <Text size={12} color="#888">Not rated yet (TODO: implementation pending rater pipeline)</Text>
             </div>
        </div>
    );
};

export default MetricsTab;